import { Box, Button, Spinner, useDisclose, VStack } from 'native-base';
import React, { ReactNode, useMemo, useState } from 'react';

import HTML from 'app/components/HTML';
import RadioChoiceDialog from 'app/components/RadioChoiceDialog';
import ReservationDetailOption from 'app/components/ReservationSummary/ReservationDetailOption';
import ReservationDetails from 'app/components/ReservationSummary/ReservationDetails';
import TicketCarouselModal from 'app/components/TicketCarouselModal';
import useAttraction from 'app/hooks/useAttraction';
import useFeatures from 'app/hooks/useFeatures';
import useGuest from 'app/hooks/useGuest';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import useRootNavigation from 'app/hooks/useRootNavigation';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import {
  EntitlementReservation,
  Reservation,
  TicketCategory,
} from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import * as ReservationHelper from 'app/services/ReservationHelper';

const CONTAINER_COLOR = 'brand.secondary';
const CONTAINER_ROUNDED = 'md';

export type ReservationSummaryProps = {
  children?: ReactNode;
  reservation: Reservation;
  attractionContentKey: string;
  orderNumber: string;
  onModifyCancel: (res: EntitlementReservation) => void;
};

export default function ReservationSummary({
  orderNumber,
  reservation: { reservationKey },
  onModifyCancel,
}: ReservationSummaryProps) {
  const navigation = useRootNavigation();
  const { t } = useI18n();
  const { reservations, activeEntitlement } = useGuest();

  const reservation =
    activeEntitlement?.reservations?.find((res) => res.reservationKey === reservationKey) ??
    reservations?.find((res) => res.reservationKey === reservationKey);

  const { attractionContent, productContent } = useAttraction({
    productContentKey: reservation?.productLineup.contentKey ?? '',
    attractionContentKey: reservation?.attractionConfig.contentKey ?? '',
  });

  const rt = useReservationTextTranslationHelper({
    attraction: attractionContent,
    product: productContent,
  });
  const ticketsModal = useDisclose();
  const ticketCategoryDialog = useDisclose();
  const [selectedTicketCategory, setSelectedTicketCategory] = useState<TicketCategory | undefined>(
    undefined
  );
  const reservationActions = useDisclose();
  const { featureEnabled } = useFeatures();
  const hasModifyReservations = useMemo(
    () => featureEnabled('modifyReservations'),
    [featureEnabled]
  );

  const canCancel = ReservationHelper.canCancel(reservation);
  const canModify = ReservationHelper.canModify(reservation?.attractionConfig, reservation);
  const canShowCancelModifyButtons = ReservationHelper.canShowCancelModifyButtons(reservation);
  const showBtns = !!reservation?.attractionOrderNumber || canCancel;

  const ticketSummaries = useMemo(
    () => ReservationHelper.getTicketSummaries(reservation, { rt }) ?? [],
    [reservation, rt]
  );

  if (!productContent || !attractionContent || !reservation) {
    return (
      <Box bg={CONTAINER_COLOR} py="24" rounded={CONTAINER_ROUNDED} testID="ReservationSummary">
        <Spinner color="white" />
      </Box>
    );
  }

  const handleViewTickets = () => {
    if (reservation.hasTicketCategories) {
      if (reservation.ticketCategories.length === 1) {
        setSelectedTicketCategory(reservation.ticketCategories[0]);
        ticketsModal.onOpen();
      } else {
        setSelectedTicketCategory(undefined);
        ticketCategoryDialog.onOpen();
      }
    } else {
      ticketsModal.onOpen();
    }
  };

  const handleTicketsClose = () => {
    if (ticketCategoryDialog.isOpen) ticketCategoryDialog.onClose();
    ticketsModal.onClose();
  };

  const handleModify = () => {
    navigation.navigate('ReservationFlow', {
      attractionKey: attractionContent.key ?? '',
      productKey: productContent.key ?? '',
      attractionName: attractionContent?.name ?? '',
      reservationKey: reservation.reservationKey ?? '',
      isModification: true,
    });
  };

  const handleCancel = () => {
    navigation.navigate('ReservationCancel', {
      reservationKey: reservation?.reservationKey || '',
    });
  };

  const handleModifyCancel = () => {
    Analytics.trackEvent(Events.AttractionModifyTickets, {
      productKey: productContent?.key ?? '',
      attractionKey: attractionContent?.key ?? '',
    });

    if (hasModifyReservations) {
      reservationActions.onOpen();
      onModifyCancel(reservation);
    } else {
      navigation.navigate('ReservationCancel', {
        reservationKey: reservation?.reservationKey ?? '',
      });
    }
  };

  return (
    <Box bg={CONTAINER_COLOR} px="4" py="6" rounded={CONTAINER_ROUNDED} testID="ReservationSummary">
      <VStack alignItems="center" justifyContent="center" space="5">
        {ticketSummaries.map(({ key, ticketCategory, optionTitle, option }, i) => (
          <VStack
            key={key}
            alignItems="center"
            borderBottomColor="white"
            borderBottomWidth={i < ticketSummaries.length - 1 ? 1 : 0}
            pb="6"
            space="4"
            w="full"
          >
            {!!optionTitle && (
              <ReservationDetailOption
                layout="horizontal"
                option={option}
                rt={rt}
                title={optionTitle}
              />
            )}

            <ReservationDetails
              passes={reservation.passes}
              reservation={reservation}
              rt={rt}
              ticketCategory={ticketCategory}
            />
          </VStack>
        ))}
        {showBtns && (
          <VStack space="2" w="full">
            {!!reservation?.attractionOrderNumber && (
              <Button variant="solidInverted" onPress={handleViewTickets}>
                {t('app_view_tickets')}
              </Button>
            )}
            {canShowCancelModifyButtons && (
              <>
                {canCancel && canModify && (
                  <Button variant="linkInverted" onPress={handleModifyCancel}>
                    {t('app_modify_cancel_reservation')}
                  </Button>
                )}

                {canCancel && !canModify && (
                  <Button variant="linkInverted" onPress={handleCancel}>
                    {rt('reservation_action_cancel')}
                  </Button>
                )}

                {canModify && !canCancel && (
                  <Button variant="linkInverted" onPress={handleModify}>
                    {rt('reservation_action_modify')}
                  </Button>
                )}
              </>
            )}
          </VStack>
        )}
      </VStack>

      {ticketsModal.isOpen && (
        <TicketCarouselModal
          isOpen={ticketsModal.isOpen}
          orderNumber={orderNumber}
          passes={reservation.passes}
          reservation={reservation}
          ticketCategory={reservation.hasTicketCategories ? selectedTicketCategory : undefined}
          onClose={handleTicketsClose}
        />
      )}

      {reservation.hasTicketCategories && ticketCategoryDialog.isOpen && (
        <RadioChoiceDialog
          body={rt('app_ticket_category_dialog_intro')}
          isOpen={ticketCategoryDialog.isOpen}
          items={ticketSummaries.map((summary) => ({
            value: summary,
            title: summary.optionTitle,
            body: <HTML html={summary.optionDetails} />,
          }))}
          title={rt('app_ticket_category_dialog_title')}
          onChange={(value) => {
            try {
              setSelectedTicketCategory(JSON.parse(value).ticketCategory);
              ticketsModal.onOpen();
            } catch (err) {
              Logger.error('failed to parse selected ticket category on selection', {
                err,
                value,
                reservationKey: reservation.reservationKey,
              });
            } finally {
              setTimeout(() => ticketCategoryDialog.onClose(), 500);
            }
          }}
          onClose={ticketCategoryDialog.onClose}
        />
      )}
      {/* <Dump data={{ now: dayjs().format('YYYY-MM-DD HH:mm:ss Z'), reservation }} /> */}
    </Box>
  );
}
